import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import type { Voucher, VoucherFormData } from '@/types/api'
import { zodResolver } from '@hookform/resolvers/zod'
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

const voucherFormSchema = z.object({
  code: z.string().min(1, 'Code is required'),
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  type: z.enum(['fixed', 'percentage']),
  discount_value: z.number().min(0, 'Discount value must be positive'),
  min_purchase: z.number().optional(),
  max_discount: z.number().optional(),
  usage_limit: z.number().min(1).optional(),
  usage_limit_per_user: z.number().min(1, 'Default is 1'),
  valid_from: z.string().min(1, 'Valid from date is required'),
  valid_until: z.string().min(1, 'Valid until date is required'),
  is_active: z.boolean(),
})

type VoucherFormSchema = z.infer<typeof voucherFormSchema>

interface VoucherFormProps {
  voucher?: Voucher | null
  onSubmit: (_data: VoucherFormData) => void
  onCancel: () => void
  isLoading?: boolean
}

export const VoucherForm: React.FC<VoucherFormProps> = ({
  voucher,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<VoucherFormSchema>({
    resolver: zodResolver(voucherFormSchema),
    defaultValues: {
      code: '',
      name: '',
      description: '',
      type: 'fixed',
      discount_value: 0,
      min_purchase: undefined,
      max_discount: undefined,
      usage_limit: undefined,
      usage_limit_per_user: 1,
      valid_from: '',
      valid_until: '',
      is_active: true,
    },
  })

  const voucherType = watch('type')

  useEffect(() => {
    if (voucher) {
      reset({
        code: voucher.code,
        name: voucher.name,
        description: voucher.description || '',
        type: voucher.type,
        discount_value: voucher.discount_value,
        min_purchase: voucher.min_purchase,
        max_discount: voucher.max_discount,
        usage_limit: voucher.usage_limit,
        usage_limit_per_user: voucher.usage_limit_per_user,
        valid_from: voucher.valid_from.split('T')[0],
        valid_until: voucher.valid_until.split('T')[0],
        is_active: voucher.is_active,
      })
    } else {
      reset({
        code: '',
        name: '',
        description: '',
        type: 'fixed',
        discount_value: 0,
        min_purchase: undefined,
        max_discount: undefined,
        usage_limit: undefined,
        usage_limit_per_user: 1,
        valid_from: '',
        valid_until: '',
        is_active: true,
      })
    }
  }, [voucher, reset])

  const onFormSubmit = (data: VoucherFormSchema) => {
    // Convert code to uppercase
    const formData = {
      ...data,
      code: data.code.toUpperCase(),
    }
    onSubmit(formData)
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>{voucher ? 'Edit Voucher' : 'Add Voucher'}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField id="code" label="Code" error={!!errors.code} required>
              <Input
                {...register('code')}
                placeholder="Enter voucher code"
                disabled={!!voucher}
                onChange={(e) => setValue('code', e.target.value.toUpperCase())}
              />
            </FormField>
            {voucher && <p className="text-xs text-gray-500 mt-1">Code cannot be changed</p>}

            <FormField id="name" label="Name" error={!!errors.name} required>
              <Input {...register('name')} placeholder="Enter voucher name" />
            </FormField>
          </div>

          <FormField id="description" label="Description">
            <Textarea
              {...register('description')}
              placeholder="Enter voucher description"
              rows={3}
            />
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField id="type" label="Type" error={!!errors.type} required>
              <Select
                value={voucherType}
                onValueChange={(value) => setValue('type', value as 'fixed' | 'percentage')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select voucher type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed Amount</SelectItem>
                  <SelectItem value="percentage">Percentage</SelectItem>
                </SelectContent>
              </Select>
            </FormField>

            <div>
              <FormField
                id="discount_value"
                label="Discount Value"
                error={!!errors.discount_value}
                required
              >
                <Input
                  {...register('discount_value', { valueAsNumber: true })}
                  type="number"
                  step="0.01"
                  min="0"
                />
              </FormField>
              <p className="text-xs text-gray-500 mt-1">
                {voucherType === 'fixed' ? 'Amount in Rupiah' : 'Percentage (e.g., 10 for 10%)'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField id="min_purchase" label="Minimum Purchase" error={!!errors.min_purchase}>
              <Input
                {...register('min_purchase', { valueAsNumber: true })}
                type="number"
                step="0.01"
                min="0"
                placeholder="Optional"
              />
            </FormField>

            {voucherType === 'percentage' && (
              <FormField id="max_discount" label="Maximum Discount" error={!!errors.max_discount}>
                <Input
                  {...register('max_discount', { valueAsNumber: true })}
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Optional"
                />
              </FormField>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormField id="usage_limit" label="Usage Limit (Total)" error={!!errors.usage_limit}>
                <Input
                  {...register('usage_limit', { valueAsNumber: true })}
                  type="number"
                  min="1"
                  placeholder="Unlimited"
                />
              </FormField>
              <p className="text-xs text-gray-500 mt-1">Leave empty for unlimited</p>
            </div>

            <FormField
              id="usage_limit_per_user"
              label="Usage Limit Per User"
              error={!!errors.usage_limit_per_user}
              required
            >
              <Input
                {...register('usage_limit_per_user', { valueAsNumber: true })}
                type="number"
                min="1"
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField id="valid_from" label="Valid From" error={!!errors.valid_from} required>
              <Input {...register('valid_from')} type="date" />
            </FormField>

            <FormField id="valid_until" label="Valid Until" error={!!errors.valid_until} required>
              <Input {...register('valid_until')} type="date" min={watch('valid_from')} />
            </FormField>
          </div>

          <div className="space-y-2">
            <Label htmlFor="is_active">Active</Label>
            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={watch('is_active')}
                onCheckedChange={(checked) => setValue('is_active', checked)}
              />
              <Label className="text-sm text-gray-600">
                Whether this voucher is currently active
              </Label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : voucher ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
